import { memo, useState, useCallback, useMemo } from "react";
import MathText from '../MathText';
import { useQuizTheme, answerTheme, marqueSigne } from './quizTheme';
import { etatsClasser } from './corrige';

/**
 * Composant interactif "Classer" côté élève.
 * Catégories en haut (zones de dépôt), chips en bas.
 * Tap chip → tap catégorie → chip placée.
 * Tap chip placée → retourne dans les disponibles.
 *
 * @param {Array} categories - [{ name: 'Pair', items: ['4','6'] }, ...] (format contenu.categories)
 * @param {string} selectedValue - Réponse existante (JSON stringifié) pour restauration
 * @param {Function} onAnswer - Callback (JSON stringifié { catName: [items] })
 * @param {Object|null} corrige - Correction sur place (lot 5, 2026-09-21), voir plus bas
 */
const ClasserEleve = memo(function ClasserEleve({ categories, selectedValue, onAnswer, onComplete, corrige = null }) {
    const clair = useQuizTheme() === 'clair';
    const t = answerTheme(clair);

    // ─── Correction sur place (lot 5, 2026-09-21) ───
    // `corrige` n'arrive que si la réponse est fausse (contrat dans corrige.jsx).
    // Chaque étiquette reste dans la boîte où il l'a posée : verte avec une
    // coche si c'est la bonne, rouge avec une croix À CÔTÉ sinon (jamais
    // barrée : un C rayé se lit €). Et l'étiquette mal placée réapparaît en
    // pointillé dans la boîte où elle allait : un seul objet, deux endroits, le
    // trajet se lit sans flèche. Les boîtes grandissent d'une ligne au plus.
    // Ce qui s'affiche est ce que Quiz a jugé, pas l'état local. Le bac
    // disparaît, tout se fige. Sans `corrige`, le rendu d'avant.
    const etats = useMemo(() => (corrige ? etatsClasser(corrige) : null), [corrige]);
    // Noms des catégories
    const categoryNames = useMemo(() => (categories || []).map(c => c.name), [categories]);

    // Tous les éléments mélangés
    const allItems = useMemo(() => {
        const items = (categories || []).flatMap(c => c.items || []);
        const shuffled = [...items];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }, [categories]);

    const totalItems = allItems.length;

    // State : éléments classés par catégorie { catName: [items] }
    const [categorized, setCategorized] = useState(() => {
        if (selectedValue) {
            try {
                const parsed = JSON.parse(selectedValue);
                if (typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
            } catch { /* ignore */ }
        }
        // Initialiser avec des tableaux vides pour chaque catégorie
        const init = {};
        for (const name of categoryNames) init[name] = [];
        return init;
    });

    // Chip sélectionnée (en attente de catégorie)
    const [selectedChip, setSelectedChip] = useState(null);

    // Pas de resync sur selectedValue : l'état est lu UNE FOIS au montage,
    // le montage est keyé par question (Quiz / ExerciseurPreview).

    // Items encore disponibles (pas classés)
    const available = useMemo(() => {
        const placed = new Set(Object.values(categorized).flat());
        // Gérer les doublons : retirer un par un
        const remaining = [...allItems];
        for (const p of placed) {
            const idx = remaining.indexOf(p);
            if (idx !== -1) remaining.splice(idx, 1);
        }
        return remaining;
    }, [allItems, categorized]);

    // Notifier le parent quand tout est classé
    const notifyAnswer = useCallback((current) => {
        const placedCount = Object.values(current).reduce((acc, arr) => acc + arr.length, 0);
        // Notifier uniquement quand tout est classé
        const isComplete = placedCount === totalItems;
        if (isComplete) onAnswer(JSON.stringify(current));
        onComplete(isComplete);
    }, [totalItems, onAnswer, onComplete]);

    // Tap chip disponible → sélectionner
    const handleChipClick = useCallback((item) => {
        setSelectedChip(prev => prev === item ? null : item);
    }, []);

    // Tap catégorie → placer la chip sélectionnée. Le nouveau classement se
    // calcule ICI, et c'est la même valeur qu'on donne à setState et au parent,
    // juste après ; jamais dans la fonction passée à setState, que React peut
    // rejouer (boucle vécue sur l'horloge le 21/09, corrigée ici le même jour).
    const handleCategoryClick = useCallback((catName) => {
        if (selectedChip === null) return;
        const next = { ...categorized, [catName]: [...(categorized[catName] || []), selectedChip] };
        setCategorized(next);
        notifyAnswer(next);
        setSelectedChip(null);
    }, [selectedChip, categorized, notifyAnswer]);

    // Tap chip placée → retirer de la catégorie
    const handleRemoveFromCategory = useCallback((catName, itemIndex) => {
        const next = { ...categorized, [catName]: (categorized[catName] || []).filter((_, i) => i !== itemIndex) };
        setCategorized(next);
        notifyAnswer(next);
    }, [categorized, notifyAnswer]);

    if (categoryNames.length === 0) return null;

    return (
        <>
            {/* Instructions (masquées en correction) */}
            {etats ? null : (
            <p className={`text-xs sm:text-sm font-medium ${t.instructions} flex items-center gap-2`}>
                <span>📦</span>
                {selectedChip
                    ? `Clique sur une catégorie pour y placer "${selectedChip}"`
                    : 'Clique sur un élément puis sur la catégorie où le placer !'
                }
            </p>
            )}

            {/* Catégories (zones de dépôt) */}
            <div className="flex gap-3 mt-3 sm:mt-4">
                {categoryNames.map((catName, ci) => {
                    const items = categorized[catName] || [];
                    const isHighlighted = selectedChip !== null;
                    const boite = etats ? (etats[catName] || { posees: [], fantomes: [] }) : null;

                    return (
                        <button
                            key={ci}
                            type="button"
                            disabled={Boolean(etats)}
                            data-testid={etats ? `boite-${ci}` : undefined}
                            onClick={() => handleCategoryClick(catName)}
                            // PAS de disabled (fix 2026-08-05) : un bouton disabled avale les
                            // clics de ses enfants — le retrait d'une étiquette placée (span
                            // interne) ne partait jamais. Le dépôt à vide est déjà bloqué
                            // dans handleCategoryClick.
                            className={`flex-1 border-2 rounded-xl p-3 text-center min-h-[90px] flex flex-col items-center gap-2 transition-all ${
                                etats
                                    ? `${t.dropzone} border-dashed cursor-default`
                                    : isHighlighted
                                        ? (clair
                                            ? 'border-blue-400 border-dashed bg-blue-50 hover:bg-blue-100 cursor-pointer'
                                            : 'border-blue-400 border-dashed bg-blue-400/5 hover:bg-blue-400/15 cursor-pointer')
                                        : `${t.dropzone} border-dashed`
                            }`}
                        >
                            <span className={`text-xs sm:text-sm font-semibold ${clair ? 'text-slate-700' : 'text-slate-300'}`}><MathText>{catName}</MathText></span>

                            {/* Correction : ses étiquettes marquées là où il les a posées, puis les fantômes */}
                            {boite ? (
                                boite.posees.length + boite.fantomes.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 justify-center">
                                        {boite.posees.map((p, ii) => {
                                            const etat = p.juste ? 'juste' : 'faux';
                                            return (
                                                <span key={`p${ii}`} data-etat={etat} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border-2 text-xs font-medium ${t[etat]}`}>
                                                    <MathText>{p.item}</MathText>
                                                    <span aria-hidden="true" className="font-black">{marqueSigne(etat)}</span>
                                                </span>
                                            );
                                        })}
                                        {boite.fantomes.map((item, ii) => (
                                            <span key={`f${ii}`} data-etat="attendu" className={`px-2.5 py-1 rounded-lg border-2 text-xs font-medium ${t.attendu}`}>
                                                <MathText>{item}</MathText>
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span className={`text-[10px] ${t.slotEmptyText}`}>Vide</span>
                                )
                            ) : items.length > 0 ? (
                                <div className="flex flex-wrap gap-1 justify-center">
                                    {items.map((item, ii) => (
                                        <span
                                            key={ii}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveFromCategory(catName, ii);
                                            }}
                                            className={`px-2.5 py-1 rounded-lg border text-xs font-medium cursor-pointer transition-all ${clair ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-blue-500/20 border-blue-400/30 text-blue-200'} ${t.removeHover}`}
                                        >
                                            <MathText>{item}</MathText>
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <span className={`text-[10px] ${t.slotEmptyText}`}>
                                    {isHighlighted ? 'Cliquer ici' : 'Vide'}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Chips disponibles (jamais en correction) */}
            {!etats && available.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center mt-3">
                    {available.map((item, i) => {
                        const isSelected = selectedChip === item;

                        return (
                            <button
                                key={`${item}-${i}`}
                                type="button"
                                onClick={() => handleChipClick(item)}
                                className={`px-4 py-2 rounded-xl border-2 text-sm sm:text-base font-medium transition-all shadow-sm ${
                                    isSelected
                                        ? `${t.selected} scale-105`
                                        : `${t.chip} cursor-pointer hover:scale-105`
                                }`}
                            >
                                <MathText>{item}</MathText>
                            </button>
                        );
                    })}
                </div>
            )}
        </>
    );
});

export default ClasserEleve;
